import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { useParams } from "react-router-dom";
import { PackagePlus, RefreshCcw } from "lucide-react";
import TransactionAttachments from "../components/TransactionAttachments";
import "../Product/customers.css";

function money(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

export default function PurchaseDetail() {
  const { companyId } = useCompany();
  const { id } = useParams();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [hdr, setHdr] = useState(null);
  const [lines, setLines] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  async function load() {
    if (!companyId || !id) return;
    setErr("");

    const { data: h, error: hErr } = await supabase
      .from("purchase_transactions")
      .select("*, vendors(name,company_name)")
      .eq("company_id", companyId)
      .eq("id", id)
      .single();

    if (hErr) {
      setErr(hErr.message);
      setHdr(null);
      return;
    }
    setHdr(h || null);

    const { data: l, error: lErr } = await supabase
      .from("purchase_transaction_lines")
      .select("*, inventory_items(id,sku,name,unit)")
      .eq("company_id", companyId)
      .eq("purchase_transaction_id", id)
      .order("created_at", { ascending: true });

    if (lErr) {
      setErr(lErr.message);
      setLines([]);
      return;
    }
    setLines(l || []);

    const { data: wh, error: whErr } = await supabase
      .from("warehouses")
      .select("id,name,is_default")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (!whErr) setWarehouses(wh || []);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, id]);

  const invLines = useMemo(() => {
    // Only lines linked to an inventory item should affect stock.
    // Support: line.inventory_item_id OR line.item_id OR line.inventory_items?.id
    return (lines || [])
      .map((l) => {
        const itemId =
          l.inventory_item_id || l.item_id || l.inventory_items?.id || null;
        const qty = Number(l.quantity || 0);
        const unitCost =
          l.unit_cost === "" ||
          l.unit_cost === null ||
          l.unit_cost === undefined
            ? null
            : Number(l.unit_cost);
        return {
          raw: l,
          itemId,
          qty,
          unitCost,
        };
      })
      .filter((x) => x.itemId && x.qty > 0);
  }, [lines]);

  const canPostInventory = useMemo(() => {
    if (!hdr) return false;
    if (hdr.posted_to_inventory) return false;
    return invLines.length > 0;
  }, [hdr, invLines.length]);

  function pickWarehouseId() {
    const txWh = hdr?.warehouse_id;
    if (txWh) return txWh;

    const def = (warehouses || []).find((w) => w.is_default);
    return def?.id || "";
  }

  async function postInventory() {
    setErr("");
    if (!companyId || !hdr?.id) return;

    const warehouseId = pickWarehouseId();
    if (!warehouseId) {
      setErr("No warehouse found. Please create a default warehouse first.");
      return;
    }

    // For Stock IN, unit_cost is REQUIRED for each line (Edge validation)
    const missingCost = invLines.find(
      (x) => x.unitCost === null || Number.isNaN(x.unitCost) || x.unitCost < 0
    );
    if (missingCost) {
      setErr(
        "One or more inventory lines are missing unit cost. Please edit the purchase lines to include unit cost."
      );
      return;
    }

    const payloadLines = invLines.map((x) => ({
      item_id: x.itemId,
      quantity: x.qty,
      unit_cost: x.unitCost,
      direction: null,
    }));

    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/inventory-post-movement`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          movement_type: "IN",
          movement_date:
            hdr.purchase_date || new Date().toISOString().slice(0, 10),
          warehouse_id: warehouseId,
          warehouse_to_id: null,
          notes: `Auto stock IN from Purchase ${hdr.id}`,
          reference_type: "purchase",
          reference_id: hdr.id,
          post_to_journal: true,
          lines: payloadLines,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }

      if (!res.ok) throw new Error(json?.error || "Failed to post inventory.");

      const { error: upErr } = await supabase
        .from("purchase_transactions")
        .update({ posted_to_inventory: true })
        .eq("company_id", companyId)
        .eq("id", hdr.id);

      if (upErr) {
        setErr(
          `Inventory posted, but could not update posted_to_inventory flag: ${upErr.message}`
        );
      } else {
        await load();
      }
    } catch (e) {
      setErr(e?.message || "Failed to post inventory.");
    } finally {
      setBusy(false);
    }
  }

  if (!hdr)
    return (
      <div className="customers-container">
        {err && <div className="banner error">{err}</div>}
        <div className="panel" style={{ padding: 14 }}>
          Loading…
        </div>
      </div>
    );

  return (
    <div className="purchase-detail-page">
      {err && <div className="purchase-detail-alert error">{err}</div>}

      <header className="purchase-detail-hero">
        <div>
          <p className="purchase-detail-eyebrow">Purchase Transaction</p>

          <h1>Purchase Details</h1>

          <p>
            {hdr.purchase_date || "—"} · {hdr.vendors?.name || "—"} ·{" "}
            {hdr.payment_status || "—"}
            {hdr.posted_to_inventory ? " · Inventory posted" : ""}
          </p>
        </div>

        <div className="purchase-detail-actions">
          <div className="purchase-detail-total">
            <span>Total Purchase</span>
            <strong>{money(hdr.total)}</strong>
          </div>

          <button
            className="purchase-detail-btn soft"
            onClick={load}
            disabled={busy}
          >
            <RefreshCcw size={16} />
            Refresh
          </button>

          {canPostInventory && (
            <button
              className="purchase-detail-btn primary"
              onClick={postInventory}
              disabled={busy}
              title="Post inventory movement for stock items on this purchase"
            >
              <PackagePlus size={16} />
              {busy ? "Posting…" : "Post Inventory"}
            </button>
          )}
        </div>
      </header>

      <section className="purchase-detail-summary-grid">
        <div className="purchase-detail-summary-card">
          <span>Vendor</span>
          <strong>{hdr.vendors?.name || "—"}</strong>
        </div>

        <div className="purchase-detail-summary-card">
          <span>Purchase Date</span>
          <strong>{hdr.purchase_date || "—"}</strong>
        </div>

        <div className="purchase-detail-summary-card">
          <span>Payment Status</span>
          <strong>{hdr.payment_status || "—"}</strong>
        </div>

        <div className="purchase-detail-summary-card">
          <span>Inventory Status</span>
          <strong>
            {hdr.posted_to_inventory
              ? "Posted"
              : invLines.length > 0
              ? "Pending"
              : "Not applicable"}
          </strong>
        </div>
      </section>

      {!canPostInventory && invLines.length > 0 && hdr.posted_to_inventory && (
        <div className="purchase-detail-note">
          Inventory movement has already been posted for this purchase.
        </div>
      )}

      {invLines.length === 0 && (
        <div className="purchase-detail-note">
          This purchase has no inventory items. Only manual/service lines were
          recorded.
        </div>
      )}

      <section className="purchase-detail-panel">
        <div className="purchase-detail-panel-head">
          <div>
            <h3>Purchase Line Items</h3>

            <p>
              Review purchased products, services, quantities and supplier
              costs.
            </p>
          </div>
        </div>

        <div className="purchase-detail-table-wrap">
          <table className="purchase-detail-table">
            <thead>
              <tr>
                <th>Item / Description</th>
                <th className="num">Qty</th>
                <th className="num">Unit Cost</th>
                <th className="num">Line Total</th>
              </tr>
            </thead>

            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    <div className="purchase-detail-item">
                      <strong>
                        {l.inventory_items
                          ? `${l.inventory_items.sku} - ${l.inventory_items.name}`
                          : l.description || "—"}
                      </strong>

                      <span>
                        {l.inventory_items
                          ? "Inventory item"
                          : "Manual/service line"}
                      </span>
                    </div>
                  </td>

                  <td className="num">{Number(l.quantity || 0)}</td>

                  <td className="num">{money(l.unit_cost)}</td>

                  <td className="num">
                    <strong>{money(l.line_total)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!lines.length && (
            <div className="purchase-detail-empty">
              No purchase line items found for this transaction.
            </div>
          )}
        </div>
      </section>

      <section className="purchase-detail-panel">
        <div className="purchase-detail-panel-head">
          <div>
            <h3>Attachments</h3>

            <p>
              Upload or review supporting documents for this purchase
              transaction.
            </p>
          </div>
        </div>

        <TransactionAttachments
          companyId={companyId}
          entityType="purchase"
          entityId={id}
        />
      </section>
    </div>
  );
}
